/**
 * Where the command palette can take a reader (DX-302).
 *
 * The palette's Navigate section lists the portal's pages, and its `>` mode
 * lists the things a reader can do. Both are spelled out here rather than
 * derived from the sidebar, because they answer different questions: the
 * sidebar is a standing column a reader scans, the palette is a list they type
 * at, and the words that make a good menu row are not always the words someone
 * would type. "Create" is a fine sidebar entry; somebody reaching for the
 * palette is more likely to type "new service".
 *
 * The keywords exist for exactly that: they are matched alongside the title so
 * a page can be found by more than its own name, without those synonyms
 * cluttering the row when it is shown.
 */

export type PaletteDestination = {
  /** The row's own text. */
  title: string;
  to: string;
  /** Matched alongside the title, never displayed. */
  keywords?: string[];
};

/**
 * The pages, in the order the navigation carries them — a reader who has the
 * sidebar in their head finds the same order here.
 */
export const NAVIGATE_TO: PaletteDestination[] = [
  { title: 'Home', to: '/', keywords: ['start', 'overview'] },
  {
    title: 'Ask',
    to: '/ask',
    // The Docs Assistant's words too: it is Ask's engine now (DX-357), and a
    // reader who knew it by that name still finds it.
    keywords: ['question', 'chat', 'assistant', 'docs assistant', 'ai', 'rag'],
  },
  { title: 'Catalog', to: '/catalog', keywords: ['services', 'components'] },
  { title: 'Docs', to: '/docs', keywords: ['documentation', 'techdocs'] },
  { title: 'APIs', to: '/api-docs', keywords: ['openapi', 'swagger', 'specs'] },
  {
    title: 'Responsibility',
    to: '/verantwortung',
    keywords: ['ownership', 'owner', 'who owns', 'verantwortung', 'teams'],
  },
  // The four fields of the responsibility map, each with a page of its own
  // (DX-180). Named by field so somebody typing "platform" reaches the field
  // rather than only the services inside it — and carrying the German word
  // too, because the document these come from is in German.
  {
    title: 'Platform',
    to: '/platform',
    keywords: ['area', 'field', 'infra', 'bereich'],
  },
  {
    title: 'Product',
    to: '/product',
    keywords: ['area', 'field', 'shop', 'cockpit', 'bereich'],
  },
  {
    title: 'DX',
    to: '/dx',
    keywords: ['area', 'field', 'developer experience', 'tooling', 'bereich'],
  },
  {
    title: 'Consulting',
    to: '/consulting',
    keywords: ['area', 'field', 'clients', 'bereich'],
  },
  {
    title: 'Status',
    to: '/status',
    keywords: ['incidents', 'uptime', 'health'],
  },
  {
    title: 'Deployments',
    to: '/deployments',
    keywords: ['deploys', 'releases'],
  },
  {
    title: 'Scorecards',
    to: '/scorecards',
    keywords: ['readiness', 'maturity'],
  },
  {
    title: 'Security',
    to: '/security',
    keywords: ['findings', 'dependabot', 'vulnerabilities', 'threats'],
  },
  { title: 'Access', to: '/access', keywords: ['permissions', 'secrets'] },
  { title: 'JAM', to: '/jam', keywords: ['local', 'runtime', 'cloud'] },
  { title: 'MCP & CLI', to: '/mcp', keywords: ['tools', 'claude', 'terminal'] },
];

/**
 * What a reader can do, reached by typing `>`.
 *
 * Deliberately short. Every entry is an action the portal can carry out today
 * — a list padded with things that are planned would make the mode read as
 * broken rather than as brief.
 */
export const ACTIONS: PaletteDestination[] = [
  {
    title: 'Create a service',
    to: '/create',
    keywords: ['new', 'scaffold', 'template'],
  },
  {
    title: 'Register an existing component',
    to: '/catalog-import',
    keywords: ['import', 'add', 'catalog-info'],
  },
  {
    title: 'Open settings',
    to: '/settings',
    keywords: ['appearance', 'theme', 'dark', 'profile'],
  },
  {
    title: 'Open notifications',
    to: '/notifications',
    keywords: ['alerts', 'inbox'],
  },
];

/**
 * Whether a destination matches what has been typed.
 *
 * Case-insensitive substring over the title and the keywords, which is the
 * right shape for a list this size: a dozen rows do not need ranking, and a
 * fuzzy matcher over twelve entries mostly surprises people. An empty query
 * matches everything, so the palette opens showing where it can take you.
 */
export const matches = (d: PaletteDestination, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) {
    return true;
  }
  return (
    d.title.toLowerCase().includes(q) ||
    // Lowercased on both sides. The keywords here happen to be lower case
    // already, so comparing them raw worked — until the first one written
    // with a capital, which would then never match and look like the
    // destination simply cannot be found.
    (d.keywords ?? []).some(k => k.toLowerCase().includes(q))
  );
};
